import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import wallpapers, { getDefaultWallpaper, applyWallpaper } from '../config/wallpapers';
import './WallpaperSelector.css';
import logger from '../utils/logger';
import storage from '../utils/LocalStorageManager';
import trackingManager from '../services/trackingManager';
import languageManager from '../utils/languageManager';

/**
 * 壁纸选择器组件
 * @param {Object} props 组件属性
 * @param {boolean} props.visible 是否可见
 * @param {function} props.onClose 关闭回调函数
 * @returns {React.Component} 壁纸选择器组件
 */
const WallpaperSelector = ({ visible, onClose }) => {
  const [selectedWallpaper, setSelectedWallpaper] = useState('');
  
  // 每次可见时从localStorage读取当前壁纸
  useEffect(() => {
    if (visible) {
      const currentWallpaper = storage.getWallpaper();
      setSelectedWallpaper(currentWallpaper || getDefaultWallpaper().value);
    }
  }, [visible]);

  if (!visible) return null;
  
  // 应用壁纸（优化版，确保立即高优先级生效）
  const handleApplyWallpaper = (wallpaper) => {
    // 立即应用壁纸样式，高优先级
    requestAnimationFrame(() => {
      // 使用封装的applyWallpaper方法
      applyWallpaper(wallpaper);
      
      // 保存到本地存储
      try {
        storage.setWallpaper(wallpaper.value);
        // 同时保存完整的壁纸对象信息（用于高斯模糊等效果）
        storage.setItem('wallpaper_object', JSON.stringify(wallpaper));
        
        // 派发事件通知其他组件壁纸已更改
        window.dispatchEvent(new CustomEvent('wallpaperChanged', { 
          detail: { wallpaper: wallpaper.value } 
        }));
        
        // 上报壁纸切换埋点
        trackingManager.reportWallpaperChange(wallpaper.name);
        
        logger.log('[壁纸选择器] 已应用新壁纸:', wallpaper.name);
      } catch (error) {
        logger.error('[壁纸选择器] 保存壁纸失败:', error);
      }
    });
  };

  return ReactDOM.createPortal(
    <div className="wallpaper-overlay" onClick={onClose}>
      <div className="wallpaper-container" onClick={e => e.stopPropagation()}>
        <div className="wallpaper-header">
          <h2>{languageManager.t('wallpaperSelector.selectWallpaper')}</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="wallpaper-grid">
          {wallpapers.map(wallpaper => {
            const isSelected = selectedWallpaper === wallpaper.value;
            
            // 合并背景和样式效果
            const optionStyle = {
              background: wallpaper.value,
              backgroundSize: wallpaper.type === 'image' ? 'cover' : 'auto',
              backgroundPosition: 'center',
              ...wallpaper.style // 应用高斯模糊等样式
            };
            
            return (
              <div 
                key={wallpaper.id}
                className={`wallpaper-option ${isSelected ? 'selected' : ''}`}
                style={optionStyle}
                onClick={() => {
                  handleApplyWallpaper(wallpaper);
                  onClose();
                }}
              >
                <span className="wallpaper-name">{wallpaper.name}</span>
                {isSelected && <div className="selected-mark">✓</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.getElementById('modal-root')
  );
};

export default WallpaperSelector; 